import React, {useEffect, useState} from "react";
import './UserDetails.css';
import {Bounce, toast} from "react-toastify";
import useUpdateUser from "../../hooks/Sysadmin/useUpdateUser";
import {useRequestChangePassword} from "../../hooks/Sysadmin/useRequestChangePassword";
import {useChangePassword} from "../../hooks/Sysadmin/useChangePassword";


interface UserDetailsContentProps {
    userDetails: {
        fullName: string;
        email: string;
        mainPhone: string;
        secondaryPhone: string;
        username: string;
        role: string;
        state: string;
    } | null;
    isUserDetailsLoading: boolean;
    disableEdit: boolean;
    setDisableEdit: React.Dispatch<React.SetStateAction<boolean>>;
    onComplete: () => void;
}

const roleOptions = [
    {value: 'TECHNICIAN', label: 'Técnico'},
    {value: 'ADMINISTRATIVE', label: 'Administrativo'},
    {value: 'SUPERVISOR', label: 'Supervisor'},
    {value: 'SALES_REP', label: 'Atención a clientes'},
    {value: 'SYSADMIN', label: 'Administrador del sistema'}
];

const stateOptions = [
    {value: 'ACTIVE', label: 'Activo'},
    {value: 'DISABLED', label: 'Deshabilitado'},
    {value: 'BLOCKED', label: 'Bloqueado'}
];

const UserDetailsContent: React.FC<UserDetailsContentProps> = ({
                                                                   userDetails,
                                                                   isUserDetailsLoading,
                                                                   disableEdit,
                                                                   setDisableEdit,
                                                                   onComplete
                                                               }) => {

    const [localFullName, setLocalFullName] = useState<string>(userDetails?.fullName || "");
    const [localEmail, setLocalEmail] = useState<string>(userDetails?.email || "");
    const [localMainPhone, setLocalMainPhone] = useState<string>(userDetails?.mainPhone || "");
    const [localSecondaryPhone, setLocalSecondaryPhone] = useState<string>(userDetails?.secondaryPhone || "");
    const [localUsername, setLocalUsername] = useState<string>(userDetails?.username || "");
    const [localRole, setLocalRole] = useState<string>(userDetails?.role || "");
    const [localState, setLocalState] = useState<string>(userDetails?.state || "");
    const {updateUser, loading} = useUpdateUser();
    const [isPasswordMode, setIsPasswordMode] = useState<boolean>(false);
    const { requestChangePassword, requestChangePasswordLoading } = useRequestChangePassword();

    const [passwordRequestCode, setPasswordRequestCode] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const {requestUpdatePassword, changePasswordLoading} = useChangePassword();


    const validateFullName = (fullName: string) => {
        const fullNameRegex = /^.{1,50}$/; // Allows any character but limits length
        return fullNameRegex.test(fullName);
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,186}\.[^\s@]+$/;
        return email.length <= 250 && emailRegex.test(email);
    };

    const validatePhone = (phone: any) => {
        const phoneRegex = /^[+]?[0-9]{10,15}$/;
        return phoneRegex.test(phone);
    };

    const validatePassword = (password: string) => {
        const allowedSymbols = "!#$%&*?.";
        const passwordRegex = new RegExp(
            `^(?=.*[A-Z])(?=.*[${allowedSymbols.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}])(?=.*[a-z])(?=.*[0-9])[A-Za-z0-9${allowedSymbols}]{8,20}$`
        );

        return passwordRegex.test(password);
    };

    const handleRequestPasswordChange = () => {
        requestChangePassword(userDetails?.username).then((response:any) => {
            toast.success(response, {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            })
        }).catch((error: any) => {
            toast.error(error.message || 'Error desconocido al actualizar la contraseña.', {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
        });
    }

    useEffect(() => {
        if (userDetails) {
            setLocalFullName(userDetails.fullName || "");
            setLocalEmail(userDetails.email || "");
            setLocalMainPhone(userDetails.mainPhone || "");
            setLocalSecondaryPhone(userDetails.secondaryPhone || "");
            setLocalUsername(userDetails.username || "");
            setLocalRole(userDetails.role || "");
            setLocalState(userDetails.state || "");
            setIsPasswordMode(false)
        }
    }, [userDetails]);

    const handleDisableEditUserClick = () => {
        if (userDetails) {
            if (isPasswordMode) {
                setDisableEdit(true);
                setIsPasswordMode(false);
                return;
            }
            setDisableEdit(!disableEdit);
        }
    };

    const handleUserUpdate = () => {

        if (!userDetails) {
            return;
        }

        if (!validateFullName(localFullName)) {
            toast.error("El nombre completo no puede exceder los 80 caracteres ni puede ir vacío.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }

        if (!validateEmail(localEmail)) {
            toast.error("Ingresar correo electrónico válido.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }

        if (!validatePhone(localMainPhone)) {
            toast.error("El teléfono principal no es válido. Por favor, ingrese un número válido.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }

        if (localSecondaryPhone && !validatePhone(localSecondaryPhone)) {
            toast.error("El teléfono secundario no es válido. Por favor, ingrese un número válido.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }

        const toBeUpdated = {
            username: localUsername,
            fullName: localFullName,
            email: localEmail,
            mainPhone: localMainPhone,
            secondaryPhone: localSecondaryPhone,
            role: localRole,
            state: localState,
        }

        if (userDetails?.username) {
            setDisableEdit(true);
            updateUser(userDetails.username, toBeUpdated).then(() => {
                toast.success('¡Usuario actualizado correctamente!', {
                    position: 'top-right',
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: 'colored',
                    transition: Bounce,
                });
                setTimeout(() => {
                    onComplete()
                }, 500)
            }).catch((error: any) => {
                toast.error(error.message || 'Error desconocido al actualizar el usuario.', {
                    position: 'top-right',
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: 'colored',
                    transition: Bounce,
                });
            })
        } else {
            toast.error("Problema al actualizar el usuario.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
        }
        setDisableEdit(true);
    }

    const handlePasswordModeToggle = () => {
        if(userDetails){
            setIsPasswordMode(true);
            setDisableEdit(true);
        }

    };

    const handlePasswordReset = () => {

        if (!validatePassword(newPassword)) {
            toast.error("La contraseña debe tener entre 8 y 20 caracteres, incluir una letra mayúscula, un número y un símbolo permitido.", {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
            return;
        }

        requestUpdatePassword(passwordRequestCode, newPassword).then((response) => {
            toast.success(response, {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            })
            setPasswordRequestCode("");
            setNewPassword("");
        }).catch((error) => {
            toast.error(error.message, {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: 'colored',
                transition: Bounce,
            });
        })
    }

    return (
        <div className="user-details-container">
            {isUserDetailsLoading ? (
                <div className="loading-overlay-for-user-details">
                    <div className="spinner"></div>
                    <div className="loading-message">Cargando información</div>
                </div>
            ) : loading ? (
                <div className="loading-overlay-for-user-details">
                    <div className="spinner"></div>
                    <div className="loading-message">Guardando cambios</div>
                </div>
            ) : (
                <div className={`user-details`}>
                    <div className="sysadmin-users-header">
                        <div className="user-details-header-title">
                            <p>USUARIO SELECCIONADO</p>
                        </div>
                        <span
                            className={`material-icons-outlined user-details-icon ${
                                !disableEdit ? "active-edit" : ""
                            }`}
                            onClick={handleDisableEditUserClick}
                        >
                        edit
                    </span>
                        <span
                            className={`material-icons-outlined user-details-icon ${
                                isPasswordMode ? "active-edit" : ""
                            }`}
                            onClick={handlePasswordModeToggle}
                        >
                        password
                    </span>
                    </div>
                    {isPasswordMode && userDetails ? (
                        <div className="request-reset-password-wrapper">
                            {requestChangePasswordLoading ||  changePasswordLoading ? (
                                <div className="loading-overlay-for-user-details">
                                    <div className="spinner"></div>
                                    <div className="loading-message">
                                        {requestChangePasswordLoading ? "Generando código..." : "Actualizando contraseña..."}
                                    </div>
                                </div>
                            ) : (
                                <div className="request-reset-password-container">
                                    <p className="password-info-message">
                                        Al dar click al botón 'Generar código', se mandará un correo al
                                        adminstrador del sistema con una clave para cambiar la contraseña del usuario
                                        seleccionado.
                                        Una vez mandado, el código tiene una expiración de 15 minutos. Ingresar abajo el
                                        código para efectuar
                                        el cambio de contraseña.<br/><br/>
                                        Usuario: {userDetails.username}<br/>
                                        Nombre: {userDetails.fullName}
                                    </p>
                                    <div className="request-password-fields">
                                        <div className="user-attribute">
                                            <div className="attribute-label">CÓDIGO</div>
                                            <input
                                                className="attribute-value"
                                                type="text"
                                                value={passwordRequestCode}
                                                onChange={(e) => setPasswordRequestCode(e.target.value)}
                                            />
                                        </div>
                                        <div className="user-attribute">
                                            <div className="attribute-label">CONTRASEÑA</div>
                                            <input
                                                className="attribute-value"
                                                type="text"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                            />
                                        </div>
                                        <p className="password-info-message">
                                            La contraseña debe tener entre 8 y 20 caracteres,sin espacios, incluir al
                                            menos una letra mayúscula, un
                                            número y uno de los siguientes símbolos permitidos: ! # $ % . & * ?
                                        </p>
                                        <div className="request-password-buttons">
                                            <button
                                                className="request-reset-password"
                                                onClick={handleRequestPasswordChange}
                                            >
                                                Generar código
                                            </button>
                                            <button
                                                className="save-new-password"
                                                onClick={handlePasswordReset}
                                                disabled={!passwordRequestCode || !newPassword}
                                            >
                                                Guardar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        userDetails && (
                            <div className="user-details-content">
                                <h4>Usuario: {localUsername}</h4>
                                <div className="user-attribute">
                                    <div className="attribute-label">NOMBRE</div>
                                    <input
                                        className="attribute-value"
                                        type="text"
                                        value={localFullName}
                                        disabled={disableEdit}
                                        onChange={(e) => setLocalFullName(e.target.value)}
                                    />
                                </div>
                                <div className="user-attribute">
                                    <div className="attribute-label">EMAIL</div>
                                    <input
                                        className="attribute-value"
                                        type="text"
                                        value={localEmail}
                                        disabled={disableEdit}
                                        onChange={(e) => setLocalEmail(e.target.value)}
                                    />
                                </div>
                                <div className="user-attribute">
                                    <div className="attribute-label">TEL. PRINCIPAL</div>
                                    <input
                                        className="attribute-value"
                                        type="text"
                                        value={localMainPhone}
                                        disabled={disableEdit}
                                        onChange={(e) => setLocalMainPhone(e.target.value)}
                                    />
                                </div>
                                <div className="user-attribute">
                                    <div className="attribute-label">TEL. SECUNDARIO</div>
                                    <input
                                        className="attribute-value"
                                        type="text"
                                        value={localSecondaryPhone}
                                        disabled={disableEdit}
                                        onChange={(e) => setLocalSecondaryPhone(e.target.value)}
                                    />
                                </div>
                                <div className="user-attribute">
                                    <div className="attribute-label">ROL</div>
                                    <select
                                        id="role"
                                        value={localRole}
                                        onChange={(e) => setLocalRole(e.target.value)}
                                        disabled={disableEdit}
                                    >
                                        {roleOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="user-attribute">
                                    <div className="attribute-label">ESTADO</div>
                                    <select
                                        id="state"
                                        value={localState}
                                        onChange={(e) => setLocalState(e.target.value)}
                                        disabled={disableEdit}
                                    >
                                        {stateOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="update-user-button">
                                    <button
                                        className="save-new-password"
                                        onClick={handleUserUpdate}
                                        disabled={disableEdit}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default UserDetailsContent;
